import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { EditOutlined, PlusOutlined, ReloadOutlined, TagOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import HasPermissions from "../../../../../containers/HasPermissions";
import { getDivisionName, isUserMultiTenant, shouldShowDivision } from "../../../../../swzutils/UserPermissionsUtil";
import VehicleHirePaymentMethod from "../../../enums/VehicleHirePaymentMethod";
import HelpLabel from "../../../components/HelpLabel";
import TemplateToVehicleHireModal from "./TemplateToVehicleHireModal";
import AddressSuburbPopover from "../../consignments/AddressSuburbPopover";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
import { showSuccessNotification } from "../../../../../swzutils";
const VehicleHireTemplateTable = () => {
    const tableRef = useRef(null);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [stateCreateVehicleHireFromTemplate, setStateCreateVehicleHireFromTemplate] = useState(null);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const showCreateModal = (vehicleHireTemplate) => {
        setStateCreateVehicleHireFromTemplate(vehicleHireTemplate);
    };
    const hideCreateModal = () => {
        setStateCreateVehicleHireFromTemplate(null);
    };
    const divisionName = getDivisionName(loggedInUser, selectedTenant);
    const columns = [{
            title: 'Pay by',
            key: 'paymentMethod',
            dataIndex: 'paymentMethod',
            width: "5%",
            filter: {
                type: 'enum',
                enums: VehicleHirePaymentMethod.filtersArray(),
                filterMultiple: false,
                requestKey: 'paymentMethod'
            },
            render: (text /*, record*/) => {
                const enumValue = VehicleHirePaymentMethod.valueOf(text);
                if (enumValue) {
                    return enumValue.renderTag();
                }
                return text;
            }
        }, {
            title: 'Start Location',
            key: 'startingSenderReceiver',
            width: "10%",
            render: (text, record) => {
                if (record.startingSenderReceiver) {
                    return (_jsx(AddressSuburbPopover, { address: record.startingSenderReceiver.address, title: record.startingSenderReceiver.companyName, isPickup: false }));
                }
                return null;
            }
        }, {
            title: 'Delivery Location',
            key: 'deliverySenderReceiver',
            width: "10%",
            render: (text, record) => {
                if (record.deliverySenderReceiver) {
                    return (_jsx(AddressSuburbPopover, { address: record.deliverySenderReceiver.address, title: record.deliverySenderReceiver.companyName, isPickup: false }));
                }
                return null;
            }
        }, {
            title: 'Description',
            key: 'description',
            dataIndex: 'description',
            width: '15%'
        }, {
            title: 'Vehicle Type',
            key: 'preferredVehicleTypeId',
            dataIndex: ['preferredVehicleType', 'description'],
            width: "10%",
        }, {
            title: 'Profile',
            dataIndex: ['tenant', 'name'],
            sorter: false,
            width: '6%',
            condition: isUserMultiTenant(loggedInUser)
        }, {
            title: divisionName,
            dataIndex: 'division',
            sorter: false,
            width: '5%',
            filter: {
                type: 'text',
                placeholder: divisionName,
                requestKey: 'divisionNameLike'
            },
            condition: shouldShowDivision(loggedInUser, selectedTenant),
            render: (text, record) => {
                if (record.division) {
                    return _jsx(Tag, { children: record.division.name }, record.division.id);
                }
                return null;
            }
        }, {
            title: 'Rego',
            dataIndex: 'vehicleRegistration',
            width: "5%",
            className: "nowrap",
            filter: {
                type: 'text',
                placeholder: 'Registration',
                requestKey: 'registrationLike',
                forceUppercase: true
            },
            render: (text, record) => {
                return record.allocatedVehicle ? _jsx("span", { children: record.allocatedVehicle.registration }) : null;
            },
        }, {
            title: 'Partner',
            dataIndex: 'tenant',
            width: "9%",
            render: (text, record) => {
                if (record.allocatedVehicle) {
                    return record.allocatedVehicle.tenantName;
                }
                return null;
            }
        }, {
            title: 'Allocated Type',
            dataIndex: ['allocatedVehicle', 'type', 'description'],
            sorter: false,
            width: '10%'
        }, {
            title: 'Action',
            width: "10%",
            render: (text, record) => {
                return (_jsx("span", { className: "actions", children: _jsx(PermissionDropdownButton, { label: (_jsxs(_Fragment, { children: [_jsx(TagOutlined, {}), " Create Vehicle Request"] })), onClick: () => showCreateModal(record), permissions: ['admin:vehicleHireTemplate:instantiate'], menuItems: [{
                                key: 'app/vehicle-hire-template/edit',
                                permissions: ['admin:vehicleHireTemplate:update'],
                                route: `#/app/vehicle-hires/templates/edit/${record.id}`,
                                label: "Edit",
                                icon: _jsx(EditOutlined, {})
                            }] }) }));
            }
        }];
    return (_jsxs("article", { className: "article swz-fixed-table-article", children: [_jsx("h2", { className: "article-title", children: _jsx(HelpLabel, { title: "Vehicle & Driver Hire Favourites", size: "large", iconSize: "large", helpText: _jsx("div", { children: _jsx("p", { children: "Vehicle & Driver Hire Favourites allow you to easily create a Vehicle & Driver Hires" }) }) }) }), _jsxs("div", { className: "table-operations clearfix", children: [_jsx(HasPermissions, { permissions: ['admin:vehicleHireTemplate:create'], children: _jsx(Button, { href: "/#/app/vehicle-hires/templates/edit/create", title: "Vehicle & Driver Hire Favourites", icon: _jsx(PlusOutlined, {}), children: "Vehicle & Driver Hire Favourites" }) }), _jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) })] }), _jsx(PaginatedFilterableTable, { tableKey: 'vehicleHireTemplatesTable', ref: tableRef, dataUri: "/admin/v1/vehicle-hire-templates", columns: columns }), _jsx(TemplateToVehicleHireModal, { open: !!stateCreateVehicleHireFromTemplate, template: stateCreateVehicleHireFromTemplate, onCancel: hideCreateModal, onCreated: (vehicleHire) => {
                    showSuccessNotification(`Vehicle request ${vehicleHire.ref} was created.`);
                    hideCreateModal();
                } })] }));
};
export default VehicleHireTemplateTable;
